import axios from "axios";
import "./interceptors";
import { toast } from "react-toastify";
import { uploadViaPresign } from "./upload";

// Adapter layer — the new API's `AdminSchedulerParlourRow`/`AdminSellParlourRow`
// are camelCase and (for the scheduler list) never leave `sell_parlour` out —
// CardParlour.jsx reads `parlour.sell_parlour.video_intro_url` unconditionally,
// so the reshape below always supplies an object, never null/undefined.
function toLegacyParlour(row) {
  return {
    id: row.id,
    live: row.live,
    poster_url: row.posterUrl,
    suite_title: row.title,
    description: row.description,
    start_date: row.startDate,
    is_public: row.isPublic,
    community_tab: row.communityTab,
    owner: { name: row.hostName, profile_image_url: row.hostAvatarUrl },
    guest: row.guest ? { name: row.guest.name, profile_image_url: row.guest.avatarUrl } : null,
    sell_parlour: { video_intro_url: row.sellParlour ? row.sellParlour.videoIntroUrl : null },
  };
}

function toLegacyParticipant(row) {
  return {
    name: row.name,
    email: row.email,
    profile_image_url: row.avatarUrl,
  };
}

function toLegacySellParlourRow(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    status: row.status,
    price: row.price,
    is_top: row.isTop,
    video_url: row.videoUrl,
    video_intro_url: row.videoIntroUrl,
    user: { name: row.authorName },
    scheduler_parlour: row.schedulerParlour
      ? {
          id: row.schedulerParlour.id,
          poster_url: row.schedulerParlour.posterUrl,
          start_date: row.schedulerParlour.startDate,
        }
      : null,
  };
}

function toLegacyPagination(meta) {
  return {
    totalRecords: meta.total,
    totalPages: meta.totalPages,
    currentPage: meta.page,
    pageSize: meta.limit,
  };
}

function toLegacyTransaction(row) {
  return {
    id: row.id,
    user: { name: row.buyerName, profile_image_url: row.buyerAvatarUrl },
    amount: row.amount,
    created_at: row.createdAt,
    status: row.status,
  };
}

export function getSchedulerParlourList() {
  const url = `api/v1/admin/scheduler-parlours?page=1&limit=100`;

  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const data = res.data.data.map(toLegacyParlour);
        dispatch({
          type: "GET_SCHEDULER_PARLOUR_LIST",
          data,
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function deleteSchedulerParlour(parlourId) {
  const url = `api/v1/admin/scheduler-parlours/${parlourId}`;

  return (dispatch) => {
    return axios.delete(url).then(() => {
      const data = { msg: "Parlour deleted successfully" };
      dispatch({
        type: "DELETE_SCHEDULER_PARLOUR",
        data,
      });
      return data;
    });
  };
}

export function getSchedulerParlourParticipants(parlourId) {
  const url = `api/v1/admin/scheduler-parlours/${parlourId}/participants`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => ({ data: res.data.map(toLegacyParticipant) }))
      .catch((err) => {
        throw err;
      });
  };
}

export function updateSchedulerParlour(parlourId, data) {
  const url = `api/v1/admin/scheduler-parlours/${parlourId}`;
  // The card sends legacy snake_case toggle keys (`is_public`/`community_tab`)
  // — translate to the new API's camelCase flag names.
  const body = {};
  if ("is_public" in data) body.isPublic = data.is_public;
  if ("community_tab" in data) body.communityTab = data.community_tab;
  // Already camelCase — the reschedule control speaks the API's own language.
  if ("scheduledAt" in data) body.scheduledAt = data.scheduledAt;

  return (dispatch) => {
    return axios
      .patch(url, body)
      .then((res) => {
        const shaped = { data: toLegacyParlour(res.data) };
        dispatch({
          type: "UPDATE_SCHEDULER_PARLOUR",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        throw err;
      });
  };
}

// Schedule a Parlour on a producer's behalf.
//
// The Create Parlour form has always posted to legacy's
// `POST /admin/scheduler-parlour/create`; v2 had no equivalent, so this was a
// stub that just explained itself. `POST /admin/scheduler-parlours` now exists
// and delegates to the SAME service the app's own create uses, so an
// admin-authored Parlour is identical to a self-served one.
//
// Maps the form's legacy shape onto the API's DTO:
//   user_id -> userId, suite_title -> title, start_date -> scheduledAt,
//   price (DOLLARS) -> amountCents, and the poster/intro files are uploaded
//   through the presigned flow first (the API takes S3 keys, never bytes).
export function createSchedulerParlour(form) {
  return async () => {
    try {
      const [posterKey, introVideoKey] = await Promise.all([
        form.poster ? uploadViaPresign(form.poster, "poster") : Promise.resolve(undefined),
        form.video_intro ? uploadViaPresign(form.video_intro, "video") : Promise.resolve(undefined),
      ]);
      const payload = {
        userId: Number(form.user_id),
        title: form.suite_title,
        amountCents: Math.round(Number(form.price || 0) * 100),
        scheduledAt: new Date(form.start_date).toISOString(),
        ...(posterKey ? { posterKey } : {}),
        ...(introVideoKey ? { introVideoKey } : {}),
      };
      const res = await axios.post(`api/v1/admin/scheduler-parlours`, payload);
      toast("Parlour created successfully", { type: "success", autoClose: 5000 });
      return res;
    } catch (error) {
      const message =
        (error && error.response && error.response.data && error.response.data.message) ||
        "Couldn't create the Parlour. Please try again.";
      toast(message, { type: "error", autoClose: 1000 * 10 });
      return false;
    }
  };
}

export function getSellParlours(data = {}) {
  const { search, author, is_top, page, pageSize } = data;
  const params = new URLSearchParams();
  params.set("page", page || 1);
  params.set("limit", pageSize || 10);
  if (search) params.set("search", search);
  if (author) params.set("author", author);
  if (is_top === "true" || is_top === "false") params.set("isTop", is_top);
  const url = `api/v1/admin/sell-parlours?${params.toString()}`;

  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const shaped = {
          data: res.data.data.map(toLegacySellParlourRow),
          pagination: toLegacyPagination(res.data.meta),
        };
        dispatch({
          type: "GET_SELL_PARLOURS",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function getSellParlourDetail(id) {
  const url = `api/v1/admin/sell-parlours/${id}/report`;

  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const r = res.data;
        const shaped = {
          id: r.id,
          title: r.title,
          description: r.description,
          price: r.price,
          status: r.status,
          // Report's own `durationMs` is null unless the live session
          // recorded both a start and end telemetry stamp.
          duration: r.durationMs || 0,
          num_reviews: r.numReviews,
          num_participants: r.numParticipants,
          collected: r.grossRevenue,
          netProfit: r.netProfit,
          revenue: r.hostRevenue,
          video_url: r.videoUrl,
          scheduler_parlour: r.schedulerParlour
            ? { start_date: r.schedulerParlour.startDate, poster_url: r.schedulerParlour.posterUrl }
            : null,
          user: {
            name: r.user.name,
            email: r.user.email,
            profile_image_url: r.user.avatarUrl,
          },
        };
        dispatch({
          type: "GET_SELL_PARLOUR_DETAIL",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          const { message } = err.response.data;
          if (message) {
            toast(message, { type: "error", autoClose: 1000 * 10 });
          }
          return;
        }
      });
  };
}

export function getTransactionsSellParlour({ id, page, pageSize }) {
  const params = new URLSearchParams();
  params.set("page", page || 1);
  params.set("limit", pageSize || 5);
  const url = `api/v1/admin/sell-parlours/${id}/transactions?${params.toString()}`;

  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const shaped = {
          data: res.data.data.map(toLegacyTransaction),
          pagination: toLegacyPagination(res.data.meta),
        };
        dispatch({
          type: "GET_SELL_PARLOUR_TRANSACTIONS",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          const { message } = err.response.data;
          if (message) {
            toast(message, { type: "error", autoClose: 1000 * 10 });
          }
          return;
        }
      });
  };
}

export function updateSellParlour(id, data) {
  const url = `api/v1/admin/sell-parlours/${id}`;
  const body = {};
  if ("is_top" in data) body.isTop = data.is_top;

  return (dispatch) => {
    return axios
      .patch(url, body)
      .then(() => {
        const shaped = { msg: "Parlour updated successfully" };
        dispatch({
          type: "UPDATE_SELL_PARLOUR",
          data: shaped,
        });
        toast(shaped.msg, { type: "success", autoClose: 1000 * 10 });
        return shaped;
      })
      .catch((err) => {
        toast("Failed to update parlour", { type: "error", autoClose: 1000 * 10 });
        throw err;
      });
  };
}
